import React, { useState, useEffect } from 'react';
import { config } from '../config';
import ImageControls from './ImageControls';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormHelperText,
  Switch,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Divide, Eye, HelpCircle, X } from 'lucide-react';
import { FileText } from 'lucide-react';
import PageHeader from './PageHeader';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';
import aiClient, { AI_MODELS } from '../api/clients/AiClient';
import imageClient, { IMAGE_MODELS } from '../api/clients/ImageClient';
import WordPressClient from '../utils/WordPressClient';
import { formatContent } from '../utils/contentFormatting';
import { useAIModels } from '../hooks/useAIModels';
import ModelSelector from './ModelSelector';
import DownloadHandler from '../components/DownloadHandler';



const MAX_RETRIES = 3;
const RETRY_DELAY = 3000;

const ArticleGenerator = ({ onGeneratingChange }) => {
  console.log('ArticleGenerator component rendering');
  
  // Sites and Categories State
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');

  // Form State
  const [titles, setTitles] = useState('');
  const [model, setModel] = useState('claude');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Queue State
  const [queuedArticles, setQueuedArticles] = useState([]);
  const [retryCount, setRetryCount] = useState({});
  
  // Image settings
  const [includeImages, setIncludeImages] = useState(true);
  const [featuredImage, setFeaturedImage] = useState(false);
  const [featuredImageModel, setFeaturedImageModel] = useState('fal-ai/flux/schnell');
  const [featuredImageStyle, setFeaturedImageStyle] = useState('realistic'); 
  const [subheadingImages, setSubheadingImages] = useState(false);
  const [subheadingImageModel, setSubheadingImageModel] = useState('fal-ai/flux/schnell');
  const [featuredDimension, setFeaturedDimension] = useState('square');
  const [subheadingDimension, setSubheadingDimension] = useState('square');
  const [subheadingImageStyle, setSubheadingImageStyle] = useState('realistic');
  const [maxSubheadings, setMaxSubheadings] = useState(5);
  const [imageFrequency, setImageFrequency] = useState('every');
  const [featuredImagePrompt, setFeaturedImagePrompt] = useState('');
  const [useAIEnhancement, setUseAIEnhancement] = useState(false);

  // Debug logging for switch states
  useEffect(() => {
    console.log('Switch states:', {
      featuredImage,
      subheadingImages,
      useAIEnhancement,
      disabled: isGenerating || loading
    });
  }, [featuredImage, subheadingImages, useAIEnhancement, isGenerating, loading]);

  // Debug logging for switch interactions
  const handleSwitchChange = (name, value) => {
    console.log(`Switch ${name} clicked:`, {
      newValue: value,
      isGenerating,
      loading,
      disabled: isGenerating || loading
    });
  };

  // Add immediate console log after state initialization
  console.log('Initial state values:', {
    loading,
    isGenerating,
    featuredImage,
    subheadingImages,
    useAIEnhancement
  });

  // Navigation Protection State
  
  const [navigationTarget, setNavigationTarget] = useState(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [navDialogOpen, setNavDialogOpen] = useState(false);

  console.log('Initial image options state:', {
    featuredImage,
    subheadingImages,
    useAIEnhancement
  });

  // Prompt State
  const [prompts, setPrompts] = useState({
    systemTemplates: [],
    userPrompts: []
  });
  const [selectedPrompt, setSelectedPrompt] = useState('');
  

  // Validation State
  const [titlesTouched, setTitlesTouched] = useState(false);
  const hasTitles = titles.trim().length > 0;
  const showTitleError = titlesTouched && !hasTitles;
  
  // Preview Queue State
  const [previewQueue, setPreviewQueue] = useState([]);

  // Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState(null);
  const [openPromptDialog, setOpenPromptDialog] = useState(false);
  const [tempPrompt, setTempPrompt] = useState('');
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const [excludeIntro, setExcludeIntro] = useState(false);
  const [excludeConclusion, setExcludeConclusion] = useState(false);
  const [excludeFAQ, setExcludeFAQ] = useState(false);
  const [maxSubheadingImages, setMaxSubheadingImages] = useState(5);

 

  // Auth Context
  const { authenticatedRequest, error: authError, loading: authLoading, isAuthenticated } = useAuth();
  const { models: availableModels, loading: modelsLoading, error: modelsError } = useAIModels(authenticatedRequest);

  const isQueueCompleted = (articles) => {
    return articles.length > 0 && articles.every(article => article.status === 'completed');
  };

  const getAuxiliaryModel = () => {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return availableModels[0]?.value;
  };

  const NavigationDialog = () => (
    <Dialog
      open={navDialogOpen}
      onClose={() => setNavDialogOpen(false)}
      aria-labelledby="nav-dialog-title"
      aria-describedby="nav-dialog-description"
    >
      <DialogTitle id="nav-dialog-title">
        Leave Page?
      </DialogTitle>
      <DialogContent>
        <Typography id="nav-dialog-description">
          Articles are still being generated. If you leave now, all progress will be lost and remaining articles will not be generated.
        </Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          Are you sure you want to leave this page?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNavDialogOpen(false)}>
          Stay on Page
        </Button>
        <Button
          onClick={() => {
            setIsGenerating(false);
            setNavDialogOpen(false);
            window.history.back();
          }}
          color="error"
          variant="contained"
        >
          Leave Page
        </Button>
      </DialogActions>
    </Dialog>
  );

  useEffect(() => {
    console.log('Loading state changed:', { loading, isGenerating });
  }, [loading]);
   
  useEffect(() => {
    console.log('Generating state changed:', { loading, isGenerating });
  }, [isGenerating]);
  
  useEffect(() => {
    if (!authLoading) {
      console.log('Starting initialization...');
      
      const initialize = async () => {
        try {
          // Initialize clients
          await initializeClients();
          
          // Load initial data
          const [sitesData, promptsData] = await Promise.all([
            authenticatedRequest(() => apiClient.getWordPressSites()),
            authenticatedRequest(() => apiClient.getSavedPrompts())
          ]);
  
          // Set initial data
          setSites(sitesData);
          // if (sitesData.length > 0) {
          //   setSelectedSite(sitesData[0].id);
          // }
  
          if (promptsData) {
            setPrompts({
              systemTemplates: promptsData.system_templates || [],
              userPrompts: promptsData.user_prompts || []
            });
          }
        } catch (error) {
          console.error('Initialization error:', error);
          setError('Failed to initialize: ' + error.message);
        }
      };
  
      initialize();
    }
  }, [authLoading]);

  useEffect(() => {
    const handlePopState = (event) => {
      if (isGenerating) {
        // Prevent default navigation
        event.preventDefault();
        window.history.pushState(null, '', window.location.pathname);
        // Show our custom dialog
        setNavDialogOpen(true);
      }
    };

    if (isGenerating) {
      window.history.pushState(null, '', window.location.pathname);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isGenerating]);

  useEffect(() => {
    if (selectedSite && selectedSite !== 'local') {
      fetchCategories();
    }
  }, [selectedSite]);

  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isGenerating) {
        // Show custom dialog for refresh
        e.preventDefault();
        setNavDialogOpen(true);
        // The returnValue is still needed for some browsers
        e.returnValue = 'Articles are still being generated. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
  
    const handleUnload = (e) => {
      if (isGenerating) {
        // Show custom dialog for tab/window close
        e.preventDefault();
        setNavDialogOpen(true);
        return false;
      }
    };
  
    const handlePopState = (event) => {
      if (isGenerating) {
        // Prevent default navigation
        event.preventDefault();
        window.history.pushState(null, '', window.location.pathname);
        // Show custom dialog
        setNavDialogOpen(true);
      }
    };
  
    // Add all event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('popstate', handlePopState);
    
    if (isGenerating) {
      window.history.pushState(null, '', window.location.pathname);
    }
  
    onGeneratingChange?.(isGenerating);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('popstate', handlePopState);
      onGeneratingChange?.(false);
    };
  }, [isGenerating, onGeneratingChange]);

  useEffect(() => {
    if (queuedArticles.length > 0 && queuedArticles.every(article => 
      article.status === 'completed' || article.status === 'error'
    )) {
      // All articles are either completed or errored
      setIsGenerating(false);
      setLoading(false);
    }
  }, [queuedArticles]);
  

  useEffect(() => {
    if (availableModels.length > 0) {
      try {
        const initializeAI = async () => {
          const keys = await authenticatedRequest(() => apiClient.getApiKeys());
          
          // Initialize AI client
          aiClient.setApiKeys(keys);
          aiClient.updateModelConfigs(availableModels);
          
          // Initialize image client
          imageClient.setAvailableModels(availableModels);
          imageClient.setAiClient(aiClient);
          imageClient.setApiKeys(keys);
          
          // Set initial model if none selected
          if (!model && availableModels.length > 0) {
            const defaultModel = availableModels[0].value;
            console.log('Setting default model:', defaultModel);
            setModel(defaultModel);
          }
        };
  
        initializeAI();
      } catch (error) {
        console.error('Error initializing AI services:', error);
        setError('Failed to initialize AI services');
      }
    }
  }, [availableModels, authenticatedRequest, model]);
  

  const LeaveConfirmationDialog = ({ open, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Leave Page?</DialogTitle>
      <DialogContent>
        <Typography>
          Articles are still being generated. If you leave now, all progress will be lost and remaining articles will not be generated.
        </Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          Are you sure you want to leave this page?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Stay on Page
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Leave Page
        </Button>
      </DialogActions>
    </Dialog>
  );

  const shouldExcludeHeading = (headingText) => {
    const text = headingText.toLowerCase();
    if (excludeIntro && (text.includes('introduction') || text.includes('overview'))) return true;
    if (excludeConclusion && (text.includes('conclusion') || text.includes('summary'))) return true;
    if (excludeFAQ && (text.includes('faq') || text.includes('frequently asked'))) return true;
    return false;
  };

  
  const handleNavigation = (target) => {
    if (isGenerating) {
      setNavigationTarget(target);
      setLeaveDialogOpen(true);
      return false;
    }
    return true;
  };

  const initializeClients = async () => {
    try {
        console.log("Starting client initialization...");
        const keys = await authenticatedRequest(() => apiClient.getApiKeys());
        console.log("Keys received:", {
            openai: keys.openai ? "present" : "missing",
            anthropic: keys.anthropic ? "present" : "missing",
            fal: keys.fal ? "present" : "missing"
        });
        
        if (!keys.openai && !keys.anthropic && !keys.openrouter) {
            throw new Error("No AI service keys configured. Please add OpenAI or Anthropic API key in settings.");
        }

        aiClient.setApiKeys(keys);
        imageClient.setApiKeys(keys);
        console.log("Clients initialized successfully");
    } catch (error) {
        console.error("Failed to initialize AI services:", error);
        setError(error.message || 'Failed to initialize AI services. Please check your API keys.');
    }
};

const fetchInitialData = async () => {
  try {
    const [sitesData, promptsData] = await Promise.all([
      authenticatedRequest(() => apiClient.getWordPressSites()),
      authenticatedRequest(() => apiClient.getSavedPrompts())
    ]);

    setSites(sitesData);

    if (promptsData) {
      setPrompts({
        systemTemplates: promptsData.system_templates || [],
        userPrompts: promptsData.user_prompts || []
      });
    }

    return sitesData; 
  } catch (err) {
    setError('Failed to load initial data');
    console.error('Error in fetchInitialData:', err);
    setSites([]);
    setPrompts({
      systemTemplates: [],
      userPrompts: []
    });
    return [];
  }
};

const handlePreviewPrompt = (promptId) => {
  const prompt = [
    ...prompts.systemTemplates,
    ...prompts.userPrompts
  ].find(p => p.id === promptId);
  
  if (prompt) {
    setPreviewPrompt(prompt);
    setPreviewOpen(true);
  }
};


  const fetchCategories = async () => {
    if (!selectedSite) return;
    
    try {
      const categoriesData = await authenticatedRequest(() => 
        apiClient.getCategories(selectedSite)
      );
      setCategories(categoriesData);
    } catch (err) {
      setError('Failed to load categories');
      console.error('Error fetching categories:', err);
    }
  };

  const initializeQueue = (titleList) => {
    const newQueue = titleList.map(title => ({
      title,
      status: 'queued',
      message: 'Waiting to start...'
    }));
    setQueuedArticles(newQueue);
  };

  const updateArticleStatus = (title, status, message, data = {}) => {
    console.log('Updating article status:', { title, status, message, data }); // Add this for debugging
    setQueuedArticles(prev =>
      prev.map(article =>
        article.title === title
          ? { 
              ...article, 
              status, 
              message,
              ...data, 
              content: data.content,
              generatedContent: data.generatedContent
            }
          : article
      )
    );
  };

  const getModelId = (selectedModel) => {
    // Map the UI model selection to the actual model ID
    switch (selectedModel) {
      case 'claude':
        return 'claude-3-sonnet-20240222';
      case 'gpt-4':
        return 'gpt-4o-mini';
      default:
        return selectedModel; // For OpenRouter models, use the value directly
    }
  };

  const handleGenerationError = async (err, title, retryAttempt) => {
    console.error('Error generating article:', err);
    const currentRetries = retryCount[title] || 0;
  
    if (retryAttempt < MAX_RETRIES && isRetryableError(err)) {
      updateArticleStatus(
        title,
        'retrying',
        `Attempt ${retryAttempt + 1}/${MAX_RETRIES}... (${err.message})`
      );
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryAttempt)));
      return generateArticle(title, retryAttempt + 1);
    }
  
    setRetryCount(prev => ({ ...prev, [title]: currentRetries + 1 }));
    updateArticleStatus(title, 'error', err.message || 'Failed to create article');
  };


  const generateArticle = async (title, retryAttempt = 0, wpClient) => {
    try {
      updateArticleStatus(title, 'processing', 'Initializing...');
       
      const prompt = [...prompts.systemTemplates, ...prompts.userPrompts].find(p => p.id === selectedPrompt);
      if (!prompt) throw new Error('Please select a writing style template');
       
      const structuredPrompt = `${prompt.prompt_text}
    
    Article Title: ${title}
    
    -Use proper heading hierarchy (h2 for main sections)
    - Each section should be well-developed
    - Include relevant examples and details
    - Ensure logical flow between sections
    - Make content engaging and informative`;
    
      // Generate initial content
      updateArticleStatus(title, 'processing', 'Generating content...');
      const content = await aiClient.generateArticleContent({
        title,
        modelId: getModelId(model),
        customPrompt: structuredPrompt
      });
  
      let processedContent = formatContent(content);
      let images = [];
      let featuredMediaId = null;
  
      // Handle featured image for both WordPress and local
      if (featuredImage) {
        try {
          updateArticleStatus(title, 'processing', 'Generating featured image...');
          const imageData = await imageClient.generateImage({
            prompt: featuredImagePrompt || title,
            model: featuredImageModel,
            dimension: featuredDimension,
            style: featuredImageStyle,
            enhancePrompt: useAIEnhancement,
            context: {
              articleTitle: title
            }
          });
  
          if (imageData?.image_url) {
            if (selectedSite === 'local') {
              // For local files, store the image URL and add to content
              images.push({
                type: 'featured',
                url: imageData.image_url,
                alt: title,
                title: title
              });
              // Insert featured image at the top of content
              processedContent = `<figure class="featured-image">
                <img src="${imageData.image_url}" alt="${title}" title="${title}" 
                  style="max-width: 100%; height: auto; margin: 20px 0;">
              </figure>\n${processedContent}`;
            } else {
              // WordPress upload
              const response = await fetch(imageData.image_url);
              const blob = await response.blob();
              const formData = new FormData();
              formData.append('file', blob, `featured-${title.replace(/[^a-z0-9]/gi, '-')}.png`);
              formData.append('siteId', selectedSite);
  
              const uploadResult = await authenticatedRequest(() => 
                apiClient.uploadMedia(formData)
              );
              featuredMediaId = uploadResult?.media_id;
            }
          }
        } catch (error) {
          console.error('Featured image error:', error);
          updateArticleStatus(title, 'processing', 'Featured image failed, continuing...');
        }
      }
  
      
      let imagesGenerated = 0;
  
      if (subheadingImages) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(processedContent, 'text/html');
          const headings = Array.from(doc.querySelectorAll('h2'));
          let imagesGenerated = 0;
      
          console.log('Processing subheading images:', {
            totalHeadings: headings.length,
            maxImages: maxSubheadingImages
          });
      
          for (const heading of headings) {
            if (imagesGenerated >= maxSubheadingImages) break;
            if (shouldExcludeHeading(heading.textContent)) {
              console.log('Skipping excluded heading:', heading.textContent);
              continue;
            }
      
            try {
              updateArticleStatus(title, 'processing', `Generating image for section: ${heading.textContent}`);
              
              const imageData = await imageClient.generateImage({
                prompt: heading.textContent,
                model: subheadingImageModel,
                dimension: subheadingDimension,
                style: subheadingImageStyle,
                enhancePrompt: useAIEnhancement,
                context: {
                  articleTitle: title
                }
              });
      
              if (imageData?.image_url) {
                if (selectedSite === 'local') {
                  // Store image data for local download
                  images.push({
                    type: 'subheading',
                    url: imageData.image_url,
                    alt: heading.textContent,
                    title: heading.textContent,
                    section: heading.textContent
                  });
      
                  // Add image directly into the content
                  const imgHtml = `<figure class="section-image">
                    <img src="${imageData.image_url}" alt="${heading.textContent}" />
                    <figcaption>${heading.textContent}</figcaption>
                  </figure>`;
      
                  const temp = doc.createElement('div');
                  temp.innerHTML = imgHtml;
                  heading.parentNode.insertBefore(temp.firstChild, heading.nextSibling);
                  imagesGenerated++;
                } else {
                  // Existing WordPress upload code
                  const response = await fetch(imageData.image_url);
                  const blob = await response.blob();
                  const uploadResult = await wpClient.uploadMedia(
                    blob,
                    `subheading-${heading.textContent.replace(/[^a-z0-9]/g, '-')}.png`
                  );
      
                  if (uploadResult) {
                    const imgHtml = `<figure class="wp-block-image size-full">
                      <img src="${uploadResult.url}" 
                           alt="${heading.textContent}" 
                           class="wp-image-${uploadResult.id}"/>
                      <figcaption>${heading.textContent}</figcaption>
                    </figure>`;
      
                    const temp = doc.createElement('div');
                    temp.innerHTML = imgHtml;
                    heading.parentNode.insertBefore(temp.firstChild, heading.nextSibling);
                    imagesGenerated++;
                  }
                }
              }
            } catch (error) {
              console.error(`Failed to process image for heading "${heading.textContent}":`, error);
              updateArticleStatus(
                title, 
                'processing',
                `Failed to generate image for "${heading.textContent}", continuing...`
              );
            }
          }
      
          processedContent = doc.body.innerHTML;
          
          console.log('Completed subheading images:', {
            imagesGenerated,
            contentLength: processedContent.length
          });
        } catch (error) {
          console.error('Subheading images error:', error);
          updateArticleStatus(title, 'processing', 'Subheading images failed, continuing...');
        }
      }
  
      if (selectedSite === 'local') {
        // Store content and metadata for local download
        updateArticleStatus(title, 'completed', 'Ready for download', {
          content: content, 
          processedContent: processedContent, 
          images: images,
          title: title,
          timestamp: new Date().toISOString(),
          metadata: {
            generated: new Date().toISOString(),
            model: getModelId(model),
            imageSettings: {
              featured: featuredImage ? {
                model: featuredImageModel,
                style: featuredImageStyle,
                dimension: featuredDimension
              } : null,
              subheadings: subheadingImages ? {
                model: subheadingImageModel,
                style: subheadingImageStyle,
                dimension: subheadingDimension,
                count: imagesGenerated
              } : null
            }
          }
        });
      } else {
        // Create WordPress post
        updateArticleStatus(title, 'processing', 'Creating WordPress post...');
        const post = await wpClient.createPost({
          title,
          content: processedContent,
          categoryId: category,
          featuredMediaId,
          status: 'draft'
        });
  
        updateArticleStatus(title, 'completed', `Created successfully (Post ID: ${post.id})`);
      }
  
    } catch (error) {
      console.error('Error generating article:', error);
      await handleGenerationError(error, title, retryAttempt);
    } finally {
      if (queuedArticles.every(article => 
        article.status === 'completed' || article.status === 'error'
      )) {
        setIsGenerating(false);
      }
    }
  };

  const handleRetry = async (title) => {
    const currentRetries = retryCount[title] || 0;
    if (currentRetries < MAX_RETRIES) {
      setRetryCount(prev => ({ ...prev, [title]: 0 }));
      await generateArticle(title, 0);
    }
  };

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 3000; 

  const isRetryableError = (error) => {
    const message = (error.message || '').toLowerCase();
    return (
      message.includes('rate limit') ||
      message.includes('timeout') ||
      message.includes('too many requests') ||
      message.includes('service unavailable') ||
      message.includes('network') ||
      message.includes('connection')
    );
  };

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setRetryCount({});
    setIsGenerating(true);
  
    const titleList = titles.split('\n').filter(title => title.trim());
    
    if (titleList.length === 0) {
      setError('Please enter at least one title');
      setLoading(false);
      setIsGenerating(false);
      return;
    }
  
    try {
      let wpClient = null;
      
      // Only initialize WordPress client if not saving locally
      if (selectedSite !== 'local') {
        const site = sites.find(s => String(s.id) === String(selectedSite));
        if (!site) {
         
          throw new Error('Selected site not found');
        }
        
        wpClient = new WordPressClient(site);
        const credentials = await authenticatedRequest(() => 
          apiClient.getWordPressCredentials(selectedSite)
        );
        wpClient.setCredentials(credentials.username, credentials.password);
      }
  
      initializeQueue(titleList);
  
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
  
      for (const title of titleList) {
        await generateArticle(title, 0, wpClient);
      }
    } catch (error) {
      console.error('Error generating articles:', error);
      setError('Error generating articles: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (article) => {
    switch (article.status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        const retries = retryCount[article.title] || 0;
        if (retries < MAX_RETRIES) {
          return (
            <Tooltip title="Click to retry">
              <IconButton
                size="small"
                onClick={() => handleRetry(article.title)}
                sx={{ color: 'error.main' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          );
        }
        return <ErrorIcon color="error" />;
      case 'processing':
      case 'retrying':
        return <CircularProgress size={24} />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  
  const handleSavePrompt = () => {
    if (tempPrompt.trim()) {
      setOpenPromptDialog(false);
    }
  };

  // Update the preview queue when titles change
  useEffect(() => {
    const titleList = titles.split('\n').filter(title => title.trim());
    setPreviewQueue(titleList.map(title => ({
      title,
      status: 'preview',
      message: 'Will be generated when you click "Generate Articles"'
    })));
  }, [titles]);

  // Add a useEffect to log the state of the image options
  useEffect(() => {
    console.log('State values on load:', { 
      loading, 
      isGenerating, 
      authLoading,
      disabled: isGenerating || loading,
      featuredImage,
      subheadingImages,
      useAIEnhancement
    });
  }, [loading, isGenerating, authLoading, featuredImage, subheadingImages, useAIEnhancement]);

  // Add HelpDialog component at the top of the component
  const HelpDialog = () => (
    <Dialog
      open={helpDialogOpen}
      onClose={() => setHelpDialogOpen(false)}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}>
        <Box sx={{ 
          width: 40, 
          height: 40, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderRadius: '10px',
          background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
        }}>
          <HelpCircle size={24} />
        </Box>
        <Typography variant="h6">How to Use the Article Generator</Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setHelpDialogOpen(false)}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: 'text.secondary',
        }}
      >
        <X size={20} />
      </IconButton>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
              1. Choose Output Location
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select whether to save articles as local HTML files or publish them directly to a WordPress site. For WordPress, you'll need to select a category as well.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
              2. Select Writing Style
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a prompt template that defines the writing style and structure of your articles. You can preview templates to see their details.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
              3. Enter Article Titles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your article titles (one per line). Each title should be between 10-150 characters. You can generate up to 100 articles at once.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
              4. Configure AI Model
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select the AI model that will generate your content. Different models have different capabilities and writing styles.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
              5. Image Settings (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Enable Featured Images to add a main image to each article<br />
              • Enable Subheading Images to add relevant images throughout the content<br />
              • Use AI Enhancement to automatically improve image prompts
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
              6. Generate Articles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Generate Articles" to start the process. You can track progress in the queue panel on the right. For local output, you can download HTML files when generation is complete.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={() => setHelpDialogOpen(false)}
          variant="contained"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
            boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #CC4414, #E85E35)',
            }
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
 
  if (authLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Please log in to continue</Alert>
      </Box>
    );
  }
  

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <HelpDialog />
       <LeaveConfirmationDialog
  open={leaveDialogOpen}
  onClose={() => setLeaveDialogOpen(false)}
  onConfirm={() => {
    setIsGenerating(false); // Stop generation
    setLeaveDialogOpen(false);
    if (navigationTarget) {
      window.location.href = navigationTarget;
    }
  }}
/>
      
      {/* Page Header with Help Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '96%',
          height: '1px',
          background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.01), rgba(59, 130, 246, 0.2) 20%, rgba(59, 130, 246, 0.2) 80%, rgba(59, 130, 246, 0.01))',
          borderRadius: '1px',
        }
      }}>
       <PageHeader 
  category="Content Creation"
  title="Bulk Article Generator" 
          description="Create multiple high-quality articles in one go! Just input your titles, choose a prompt template, and let AI generate engaging content with optional AI-generated images for each article."
  icon={<FileText size={24} color="#E04C16" />} 
/>
        <Box sx={{ mr: 6 }}>
        <Button
          onClick={() => setHelpDialogOpen(true)}
          startIcon={<HelpCircle size={18} />}
          sx={{
              bgcolor: 'rgba(224, 76, 22, 0.08)',
              color: '#E04C16',
              borderRadius: '12px',
            px: 3,
              py: 1,
              fontWeight: 600,
              fontSize: '0.875rem',
              border: '1px solid rgba(224, 76, 22, 0.12)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease-in-out',
            '&:hover': {
                bgcolor: 'rgba(224, 76, 22, 0.12)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.12)'
            }
          }}
        >
          How to Use
        </Button>
      </Box>
        </Box>

      {authError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {authError}
        </Alert>
      )}
      <Grid container spacing={3} sx={{ px: 2 }}>
        {/* Left Column - Form Controls */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
          }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                <FormControl fullWidth required>
                    <InputLabel id="wordpress-site-label" sx={{ 
                      fontWeight: 600,
                      '&.Mui-focused': {
                        color: '#3B82F6'
                      }
                    }}>Select Output Type</InputLabel>
  <Select
    labelId="wordpress-site-label"
    value={selectedSite || ''}
    label="Output Location"
    onChange={(e) => {
      setSelectedSite(e.target.value);
      if (e.target.value === 'local') {
        setCategory('');
      }
    }}
                      disabled={loading || isGenerating}
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          transition: 'all 0.2s ease'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(59, 130, 246, 0.5)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3B82F6',
                          borderWidth: '2px'
                        },
                        '& .MuiSelect-select': {
                          padding: '14px 16px',
                        },
                        '& .MuiSelect-select .MuiBox-root': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: '12px',
                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                            mt: 1,
                            '& .MuiMenuItem-root': {
                              transition: 'all 0.1s ease'
                            }
                          }
                        }
                      }}
                      renderValue={(selected) => {
                        if (selected === 'local') {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ 
                                width: 28, 
                                height: 28, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                borderRadius: '6px',
                                background: 'linear-gradient(45deg, #10B981, #059669)',
                                color: 'white'
                              }}>
                                <FileText size={16} />
                              </Box>
                              <Typography>Save As Text/HTML</Typography>
                            </Box>
                          );
                        }
                        const site = sites.find(s => String(s.id) === String(selected));
                        if (site) {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ 
                                width: 28, 
                                height: 28, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                borderRadius: '6px',
                                background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                                color: 'white'
                              }}>
                                {site.name.charAt(0).toUpperCase()}
                              </Box>
                              <Typography>{site.name}</Typography>
                            </Box>
                          );
                        }
                        return '';
                      }}
                    >
                      <MenuItem value="local" sx={{
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': {
                          bgcolor: 'rgba(59, 130, 246, 0.04)'
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(59, 130, 246, 0.08)',
                          '&:hover': {
                            bgcolor: 'rgba(59, 130, 246, 0.12)'
                          }
                        }
                      }}>
                        <Box sx={{ 
                          width: 32, 
                          height: 32, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          borderRadius: '8px',
                          background: 'linear-gradient(45deg, #10B981, #059669)',
                          color: 'white'
                        }}>
                          <FileText size={18} />
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>Save As Text/HTML</Typography>
                          <Typography variant="caption" color="text.secondary">Download articles as HTML files</Typography>
                        </Box>
                      </MenuItem>
                      <Divider sx={{ my: 1.5 }} />
                      <ListSubheader sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem',
                        lineHeight: '2.5em',
                        bgcolor: 'transparent',
                        color: '#3B82F6'
                      }}>
     Save To WordPress Site
    </ListSubheader>
    {sites.map((site) => (
                        <MenuItem 
                          key={site.id} 
                          value={site.id}
                          sx={{
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            '&:hover': {
                              bgcolor: 'rgba(59, 130, 246, 0.04)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(59, 130, 246, 0.08)',
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.12)'
                              }
                            }
                          }}
                        >
                          <Box sx={{ 
                            width: 32, 
                            height: 32, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderRadius: '8px',
                            background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                            color: 'white'
                          }}>
                            {site.name.charAt(0).toUpperCase()}
                          </Box>
        {site.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>
                </Grid>

                <Grid item xs={12}>
                {selectedSite !== 'local' && (
  <FormControl
    fullWidth
    required
                    >
                      <InputLabel id="category-label" sx={{ 
                        fontWeight: 600,
                        '&.Mui-focused': {
                          color: '#3B82F6'
                        }
                      }}>Select Category</InputLabel>
    <Select
      labelId="category-label"
      value={category}
      label="Select Category"
      onChange={(e) => setCategory(e.target.value)}
                        disabled={loading || isGenerating || !selectedSite}
                        sx={{
                          borderRadius: '12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            transition: 'all 0.2s ease'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(59, 130, 246, 0.5)'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3B82F6',
                            borderWidth: '2px'
                          },
                          '& .MuiSelect-select': {
                            padding: '14px 16px',
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: '12px',
                              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                              mt: 1,
                              '& .MuiMenuItem-root': {
                                transition: 'all 0.1s ease'
                              }
                            }
                          }
                        }}
    >
      {categories.map((cat) => (
                          <MenuItem 
                            key={cat.id} 
                            value={cat.id}
                            sx={{
                              py: 1.5,
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.04)'
                              },
                              '&.Mui-selected': {
                                bgcolor: 'rgba(59, 130, 246, 0.08)',
                                '&:hover': {
                                  bgcolor: 'rgba(59, 130, 246, 0.12)'
                                }
                              }
                            }}
                          >
          {cat.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth required>
                      <InputLabel sx={{ 
                        fontWeight: 600,
                        '&.Mui-focused': {
                          color: '#3B82F6'
                        }
                      }}>Choose A Prompt</InputLabel>
  <Select
    value={selectedPrompt}
    label="Choose A Prompt"
    onChange={(e) => setSelectedPrompt(e.target.value)}
                        disabled={loading || isGenerating}
                        sx={{
                          borderRadius: '12px',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            transition: 'all 0.2s ease'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(59, 130, 246, 0.5)'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3B82F6',
                            borderWidth: '2px'
                          },
                          '& .MuiSelect-select': {
                            padding: '14px 16px',
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: '12px',
                              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                              mt: 1,
                              '& .MuiMenuItem-root': {
                                transition: 'all 0.1s ease'
                              }
                            }
                          }
                        }}
  >
    {/* System Templates Section */}
    {prompts.systemTemplates.length > 0 && (
      <ListSubheader 
        sx={{ 
          fontWeight: 600,
                              fontSize: '0.875rem',
                              lineHeight: '2.5em',
                              bgcolor: 'transparent',
                              color: '#3B82F6'
        }}
      >
        Default Prompts
      </ListSubheader>
    )}
    {prompts.systemTemplates.map((prompt) => (
      <MenuItem 
        key={prompt.id} 
        value={prompt.id}
                            sx={{
                              py: 1.5,
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.04)'
                              },
                              '&.Mui-selected': {
                                bgcolor: 'rgba(59, 130, 246, 0.08)',
                                '&:hover': {
                                  bgcolor: 'rgba(59, 130, 246, 0.12)'
                                }
                              }
                            }}
      >
        <Box>
          <Typography variant="subtitle2">{prompt.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {prompt.settings?.description || 'System template'}
          </Typography>
        </Box>
      </MenuItem>
    ))}

    {/* User Templates Section */}
    {prompts.userPrompts.length > 0 && (
      <ListSubheader 
        sx={{ 
          fontWeight: 600,
                              fontSize: '0.875rem',
                              lineHeight: '2.5em',
                              bgcolor: 'transparent',
                              color: '#3B82F6'
        }}
      >
        My Saved Prompts
      </ListSubheader>
    )}
    {prompts.userPrompts.map((prompt) => (
                          <MenuItem 
                            key={prompt.id} 
                            value={prompt.id}
                            sx={{
                              py: 1.5,
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.04)'
                              },
                              '&.Mui-selected': {
                                bgcolor: 'rgba(59, 130, 246, 0.08)',
                                '&:hover': {
                                  bgcolor: 'rgba(59, 130, 246, 0.12)'
                                }
                              }
                            }}
                          >
        {prompt.name}
      </MenuItem>
    ))}

    {/* Show message if no prompts available */}
    {!prompts.systemTemplates.length && !prompts.userPrompts.length && (
      <MenuItem disabled>
        No templates available
      </MenuItem>
    )}
  </Select>
</FormControl>
                    {selectedPrompt && (
                      <IconButton 
                        onClick={() => handlePreviewPrompt(selectedPrompt)}
                        sx={{ 
                          ml: 1,
                          bgcolor: 'rgba(59, 130, 246, 0.08)',
                          color: '#3B82F6',
                          '&:hover': {
                            bgcolor: 'rgba(59, 130, 246, 0.16)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <Eye size={18} />
                      </IconButton>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Article Titles"
                    multiline
                    rows={5}
                    value={titles}
                    onChange={(e) => setTitles(e.target.value)}
                    onBlur={() => setTitlesTouched(true)}
                    required
                    disabled={loading || isGenerating}
                    placeholder="Enter article titles (one per line)"
                    error={showTitleError}
                    helperText={
                      showTitleError
                        ? "Please enter valid article titles"
                        : "Enter one title per line. Each title should be 10-150 characters. Maximum 100 titles."
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          transition: 'all 0.2s ease'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(59, 130, 246, 0.5)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        '&.Mui-focused': {
                          color: '#3B82F6'
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, width: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <ModelSelector
              value={model}
              onChange={(e) => setModel(e.target.value)}
              required
              tooltip="Choose the AI model that will generate your article content. Different models have different strengths and capabilities."
                        label="AI MODEL"
                        disabled={loading || isGenerating || modelsLoading}
              showDescription={true}
            />
            {modelsError && (
              <FormHelperText error>
                {modelsError}
              </FormHelperText>
            )}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Featured Image Section */}
                    <Paper sx={{ 
                      p: 3, 
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      background: 'linear-gradient(to bottom right, #FFFFFF, #F0F7FF)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                      overflow: 'hidden',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '150px',
                        height: '150px',
                        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, rgba(255, 255, 255, 0) 70%)',
                        borderRadius: '50%',
                        zIndex: 0,
                        pointerEvents: 'none'
                      }
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        mb: 2,
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderRadius: '10px',
                            background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                          }}>
                            <Eye size={20} />
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Featured Image
                          </Typography>
                        </Box>
                        <Switch
              checked={featuredImage}
                          onChange={(e) => {
                            console.log('Featured Image toggle clicked, new value:', e.target.checked);
                            setFeaturedImage(e.target.checked);
                          }}
                          disabled={isGenerating || loading}
                          sx={{
                            width: 42,
                            height: 26,
                            padding: 0,
                            position: 'relative',
                            zIndex: 2,
                            '& .MuiSwitch-switchBase': {
                              padding: 0,
                              margin: '2px',
                              transitionDuration: '300ms',
                              '&.Mui-checked': {
                                transform: 'translateX(16px)',
                                color: '#fff',
                                '& + .MuiSwitch-track': {
                                  backgroundColor: '#EC4899',
                                  opacity: 1,
                                  border: 0,
                                }
                              }
                            },
                            '& .MuiSwitch-thumb': {
                              boxSizing: 'border-box',
                              width: 22,
                              height: 22,
                            },
                            '& .MuiSwitch-track': {
                              borderRadius: 26 / 2,
                              backgroundColor: '#E9E9EA',
                              opacity: 1,
                            }
                          }}
                        />
                      </Box>
        
        {featuredImage && (
                        <Box sx={{ mt: 2, position: 'relative', zIndex: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Featured Image Prompt (optional)"
                  placeholder="Leave blank to let AI generate based on article title and content"
                  value={featuredImagePrompt}
                  onChange={(e) => setFeaturedImagePrompt(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  }
                                }}
                />
              </Grid>
              <Grid item xs={12}>
                <ImageControls
                  model={featuredImageModel}
                  setModel={setFeaturedImageModel}
                  style={featuredImageStyle}
                  setStyle={setFeaturedImageStyle}
                  dimension={featuredDimension}
                  setDimension={setFeaturedDimension}
                  loading={loading}
                />
              </Grid>
            </Grid>
          </Box>
        )}
                    </Paper>

      {/* Subheading Images Section */}
                    <Paper sx={{ 
                      p: 3, 
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      background: 'linear-gradient(to bottom right, #FFFFFF, #F0FFF4)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                      overflow: 'hidden',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '150px',
                        height: '150px',
                        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, rgba(255, 255, 255, 0) 70%)',
                        borderRadius: '50%',
                        zIndex: 0,
                        pointerEvents: 'none'
                      }
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        mb: 2,
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderRadius: '10px',
                            background: 'linear-gradient(45deg, #10B981, #059669)',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                          }}>
                            <Eye size={20} />
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Subheading Images
                          </Typography>
                        </Box>
                        <Switch
              checked={subheadingImages}
                          onChange={(e) => {
                            console.log('Subheading Images toggle clicked, new value:', e.target.checked);
                            setSubheadingImages(e.target.checked);
                          }}
                          disabled={isGenerating || loading}
                          sx={{
                            width: 42,
                            height: 26,
                            padding: 0,
                            position: 'relative',
                            zIndex: 2,
                            '& .MuiSwitch-switchBase': {
                              padding: 0,
                              margin: '2px',
                              transitionDuration: '300ms',
                              '&.Mui-checked': {
                                transform: 'translateX(16px)',
                                color: '#fff',
                                '& + .MuiSwitch-track': {
                                  backgroundColor: '#10B981',
                                  opacity: 1,
                                  border: 0,
                                }
                              }
                            },
                            '& .MuiSwitch-thumb': {
                              boxSizing: 'border-box',
                              width: 22,
                              height: 22,
                            },
                            '& .MuiSwitch-track': {
                              borderRadius: 26 / 2,
                              backgroundColor: '#E9E9EA',
                              opacity: 1,
                            }
                          }}
                        />
                      </Box>

{subheadingImages && (
                        <Box sx={{ mt: 2, position: 'relative', zIndex: 1 }}>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Configure settings for subheading images. A new image will be generated for each subheading.
        </Typography>
      </Grid>
      
      {/* Image Generation Controls */}
      <Grid item xs={12}>
        <ImageControls
          model={subheadingImageModel}
          setModel={setSubheadingImageModel}
          style={subheadingImageStyle}
          setStyle={setSubheadingImageStyle}
          dimension={subheadingDimension}
          setDimension={setSubheadingDimension}
          loading={loading}
        />
      </Grid>

      {/* Maximum Images Control */}
      <Grid item xs={12} md={6}>
      <TextField
    fullWidth
    type="number"
    label="Maximum Subheading Images"
    value={maxSubheadingImages}
    onChange={(e) => {
      const inputValue = e.target.value === '' ? '' : parseInt(e.target.value);
      if (inputValue === '') {
        setMaxSubheadingImages('');
      } else {
        const limitedValue = Math.min(Math.max(0, inputValue), 10);
        setMaxSubheadingImages(limitedValue);
      }
    }}
    InputProps={{ 
      inputProps: { 
        min: 0, 
        max: 10 
      } 
    }}
    helperText={`You can generate 0 to 10 images`}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  }
                                }}
  />
      </Grid>

      {/* Section Exclusion Controls */}
      <Grid item xs={12}>
                              <Paper 
                                elevation={0} 
                                sx={{ 
                                  p: 2, 
                                  mt: 1, 
                                  bgcolor: 'rgba(16, 185, 129, 0.04)', 
                                  borderRadius: '8px',
                                  border: '1px solid rgba(16, 185, 129, 0.1)'
                                }}
                              >
                                <Typography variant="subtitle2" gutterBottom>
          Exclude Images from Sections
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeIntro}
                onChange={(e) => setExcludeIntro(e.target.checked)}
                                        disabled={loading || isGenerating}
                                        size="small"
                                        sx={{
                                          color: 'rgba(16, 185, 129, 0.6)',
                                          '&.Mui-checked': {
                                            color: '#10B981',
                                          },
                                        }}
                                      />
                                    }
                                    label={<Typography variant="body2">Skip Introduction Section</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeConclusion}
                onChange={(e) => setExcludeConclusion(e.target.checked)}
                                        disabled={loading || isGenerating}
                                        size="small"
                                        sx={{
                                          color: 'rgba(16, 185, 129, 0.6)',
                                          '&.Mui-checked': {
                                            color: '#10B981',
                                          },
                                        }}
                                      />
                                    }
                                    label={<Typography variant="body2">Skip Conclusion Section</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeFAQ}
                onChange={(e) => setExcludeFAQ(e.target.checked)}
                                        disabled={loading || isGenerating}
                                        size="small"
                                        sx={{
                                          color: 'rgba(16, 185, 129, 0.6)',
                                          '&.Mui-checked': {
                                            color: '#10B981',
                                          },
                                        }}
                                      />
                                    }
                                    label={<Typography variant="body2">Skip FAQ Section</Typography>}
          />
        </Box>
                              </Paper>
      </Grid>
    </Grid>
  </Box>
)}
                    </Paper>
                    
                    {/* AI Enhancement Toggle */}
                    <Paper sx={{ 
                      p: 3, 
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      background: 'linear-gradient(to bottom right, #FFFFFF, #FFF1F2)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                      overflow: 'hidden',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '150px',
                        height: '150px',
                        background: 'radial-gradient(circle, rgba(244, 63, 94, 0.08) 0%, rgba(255, 255, 255, 0) 70%)',
                        borderRadius: '50%',
                        zIndex: 0,
                        pointerEvents: 'none'
                      }
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderRadius: '10px',
                            background: 'linear-gradient(45deg, #F43F5E, #E11D48)',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
                          }}>
                            <HelpCircle size={20} />
      </Box>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              AI Image Prompt Enhancement
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              AI will automatically enhance image prompts for better results
                            </Typography>
                          </Box>
                        </Box>
                        <Switch
                          checked={useAIEnhancement}
                          onChange={(e) => {
                            console.log('AI Enhancement toggle clicked, new value:', e.target.checked);
                            setUseAIEnhancement(e.target.checked);
                          }}
                          disabled={isGenerating || loading}
                          sx={{
                            width: 42,
                            height: 26,
                            padding: 0,
                            position: 'relative',
                            zIndex: 2,
                            '& .MuiSwitch-switchBase': {
                              padding: 0,
                              margin: '2px',
                              transitionDuration: '300ms',
                              '&.Mui-checked': {
                                transform: 'translateX(16px)',
                                color: '#fff',
                                '& + .MuiSwitch-track': {
                                  backgroundColor: '#F43F5E',
                                  opacity: 1,
                                  border: 0,
                                }
                              }
                            },
                            '& .MuiSwitch-thumb': {
                              boxSizing: 'border-box',
                              width: 22,
                              height: 22,
                            },
                            '& .MuiSwitch-track': {
                              borderRadius: 26 / 2,
                              backgroundColor: '#E9E9EA',
                              opacity: 1,
                            }
                          }}
                        />
                      </Box>
                    </Paper>
    </Box>
  </Grid>
                
                

                <Grid item xs={12}>
                  {error && (
                    <Alert severity="error">
                      {typeof error === 'string' ? error : 'An error occurred'}
                    </Alert>
                  )}
                </Grid>
            
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
  type="submit"
  variant="contained"
  disabled={
    (selectedSite !== 'local' && (!selectedSite || !category)) || 
    !selectedPrompt || 
    !hasTitles || 
    loading
  }
  fullWidth
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        padding: '12px 24px',
                        background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #2563EB, #1D4ED8)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)'
                        },
                        '&:disabled': {
                          background: 'linear-gradient(45deg, #E5E7EB, #F3F4F6)',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={20} sx={{ color: 'inherit' }} />
                          <span>Generating Articles...</span>
                        </Box>
                      ) : (
                        'Generate Articles'
                      )}
</Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to reset? All your changes will be lost.')) {
                          window.location.reload();
                        }
                      }}
                      disabled={isGenerating || loading}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        padding: '12px 24px',
                        borderColor: 'rgba(239, 68, 68, 0.5)',
                        color: 'rgb(239, 68, 68)',
                        '&:hover': {
                          borderColor: 'rgb(239, 68, 68)',
                          backgroundColor: 'rgba(239, 68, 68, 0.04)'
                        },
                        '&:disabled': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          color: 'rgba(0, 0, 0, 0.26)'
                        }
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
        {/* Right Column - Queue Status */}
        <Grid item xs={12} md={5}>
          {(queuedArticles.length > 0 || previewQueue.length > 0) && (
            <Paper sx={{ 
              p: 3, 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {queuedArticles.length > 0 ? (
                  <>Generation Queue {isGenerating && <CircularProgress size={16} sx={{ ml: 1 }} />}</>
                ) : (
                  <>Queue Preview</>
                )}
              </Typography>
              
              <Box sx={{ 
                overflowY: 'auto', 
                maxHeight: '600px',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0,0,0,0.03)',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.1)',
                  borderRadius: '10px',
                  '&:hover': {
                    background: 'rgba(0,0,0,0.15)',
                  }
                }
              }}>
                {queuedArticles.length > 0 ? (
                  queuedArticles.map((article, index) => (
                    <Paper 
                  key={index}
                      elevation={0}
                  sx={{
                        p: 2, 
                        mb: 2, 
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: article.status === 'completed' 
                          ? 'rgba(46, 125, 50, 0.2)' 
                          : article.status === 'error'
                            ? 'rgba(211, 47, 47, 0.2)'
                            : 'rgba(0, 0, 0, 0.08)',
                        bgcolor: article.status === 'completed' 
                          ? 'rgba(46, 125, 50, 0.04)' 
                          : article.status === 'error'
                            ? 'rgba(211, 47, 47, 0.04)'
                            : 'rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 600,
                            color: article.status === 'completed' 
                              ? 'success.main' 
                              : article.status === 'error'
                                ? 'error.main'
                                : 'text.primary',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '80%'
                          }}
                        >
                          {article.title}
                        </Typography>
                        
                        <Box>
                          {article.status === 'completed' && (
                            <CheckCircleIcon color="success" />
                          )}
                          {article.status === 'error' && (
                            <Tooltip title="Click to retry">
                              <IconButton
                                size="small"
                                onClick={() => handleRetry(article.title)}
                                sx={{ color: 'error.main' }}
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(article.status === 'processing' || article.status === 'retrying') && (
                            <CircularProgress size={24} />
                          )}
                          {article.status === 'queued' && (
                            <PendingIcon color="disabled" />
                          )}
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {article.message}
                      </Typography>
                      
                      {article.status === 'completed' && article.processedContent && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Eye size={16} />}
                            onClick={() => {
                              // Open preview dialog or handle download
                              if (selectedSite === 'local') {
                                const element = document.createElement('a');
                                const file = new Blob([article.processedContent], {type: 'text/html'});
                                element.href = URL.createObjectURL(file);
                                element.download = `${article.title.replace(/[^a-z0-9]/gi, '-')}.html`;
                                document.body.appendChild(element);
                                element.click();
                                document.body.removeChild(element);
                              }
                            }}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              borderColor: 'rgba(59, 130, 246, 0.5)',
                              color: '#3B82F6',
                              '&:hover': {
                                borderColor: '#3B82F6',
                                bgcolor: 'rgba(59, 130, 246, 0.04)'
                              }
                            }}
                          >
                            {selectedSite === 'local' ? 'Download HTML' : 'View Post'}
                          </Button>
                        </Box>
            )}
          </Paper>
                  ))
                ) : (
                  // Preview queue items
                  previewQueue.map((article, index) => (
                    <Paper 
                      key={index}
                      elevation={0}
                      sx={{ 
                        p: 2, 
                        mb: 2, 
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 600,
                            color: 'text.primary',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '80%'
                          }}
                        >
                          {article.title}
          </Typography>
                        
                        <Box>
                          <Tooltip title="Waiting to be generated">
                            <PendingIcon color="disabled" />
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {article.message}
                      </Typography>
                    </Paper>
                  ))
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
export default ArticleGenerator;